import React, { useState } from "react";
import LoadingWidget from "../../../../context_providers/modal/loader";
import Icon from "@mdi/react";
import {
  mdiAlertOutline,
  mdiCloseCircleOutline,
  mdiLockOpenOutline,
} from "@mdi/js";
import {
  StockTransferSubtype,
  WarehouseOutwardModel,
} from "../model/warehouse_outward_model";
import "../inward-styles.css";
import { formatAmountWithCommas } from "../../content_panel/utils/helpers";
import {
  DeleteRejectedeOutwardApi,
  RejectWarehouseOutwardApi,
} from "../warehouse_module_repo";
import {
  ToastType,
  useToast,
} from "../../../../context_providers/toast/toast_ctx";

type RejectSTModalProps = {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  selectedOutward?: WarehouseOutwardModel | null;
};

const getTotalValueFromItems = (o: WarehouseOutwardModel): number => {
  return (
    o.items?.reduce((sum, i) => sum + (i.itemPrice || 0) * i.requestedQty, 0) ??
    0
  );
};

const RejectSTModal: React.FC<RejectSTModalProps> = ({
  open,
  onClose,
  onSuccess,
  selectedOutward,
}) => {
  const [loading, setLoading] = useState(false);
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const { showToast } = useToast();

  if (!open || !selectedOutward) return null;

  const fgItems =
    selectedOutward.items?.filter(
      (item) => item.subCategory === "Finished Goods",
    ) ?? [];
  const rmItems =
    selectedOutward.items?.filter(
      (item) => item.subCategory !== "Finished Goods",
    ) ?? [];
  const hasFinishedGoodsItems = fgItems.length > 0;
  const hasRawMaterialItems = rmItems.length > 0;
  const isPureRMSt = hasRawMaterialItems && !hasFinishedGoodsItems;

  const allocationSummary = fgItems.reduce(
    (acc, item) => {
      acc.requested += Number(item.requestedQty) || 0;
      acc.allocated += Array.isArray(item.deviceIds)
        ? item.deviceIds.length
        : 0;
      return acc;
    },
    { requested: 0, allocated: 0 },
  );

  const isFullyAllocated =
    hasFinishedGoodsItems &&
    allocationSummary.requested === allocationSummary.allocated;

  // A pure-RM ST defers its inventory reservation to the Material Picking
  // Pending checkpoint (unlike a mixed FG+RM ST, whose RM lines still reserve
  // stock at creation as before) — so it has nothing to release until that
  // checkpoint is confirmed and the ST has moved past it.
  const rmAlreadyReserved =
    hasFinishedGoodsItems ||
    selectedOutward.status !== "Material Picking Pending";
  const rmReservedQty = rmItems.reduce((sum, item) => {
    const qty = hasFinishedGoodsItems
      ? item.requestedQty
      : (item.pickedQty ?? item.requestedQty);
    return sum + (Number(qty) || 0);
  }, 0);

  const isST = selectedOutward.type === "ST";
  const stSubtype = isST ? selectedOutward.st_subtype : undefined;
  const isRegularST = stSubtype === StockTransferSubtype.REGULAR;
  const isReturnableST = stSubtype === StockTransferSubtype.RETURNABLE;
  const isReplacementST = stSubtype === StockTransferSubtype.REPLACEMENT;

  const finalStatus =
    selectedOutward.type === "SO" || isReturnableST || isReplacementST
      ? "Cancelled"
      : "Rejected";

  const isRejectDisabled = loading || reason.trim().length < 10;

  const handleCancelST = () => {
    if (loading) return;
    setReason("");
    setError(null);
    onClose();
  };

  const handleRejectST = async (cancelAfter = false) => {
    setError(null);

    if (!reason.trim()) {
      setError("Rejection reason is required.");
      return;
    }

    if (reason.trim().length < 10) {
      setError("Rejection reason must be at least 10 characters.");
      return;
    }

    const payload = {
      reason: reason.trim(),
    };

    try {
      setLoading(true);

      const res = await RejectWarehouseOutwardApi(payload, selectedOutward.id);
      let cancelRes;
      if (res.success) {
        if (selectedOutward.type === "SO" || cancelAfter) {
          cancelRes = await DeleteRejectedeOutwardApi(selectedOutward.id);
        }
      }

      if (!res.success || (cancelRes && !cancelRes.success)) {
        showToast({
          type: ToastType.error,
          text: res.error || cancelRes?.error,
        });
        return;
      }

      setReason("");
      const willCancel =
        selectedOutward.type === "SO" ||
        cancelAfter ||
        finalStatus === "Cancelled";
      showToast({
        type: ToastType.success,
        text:
          selectedOutward.type === "SO"
            ? "Sales Order Closed Successfully : id - " +
              selectedOutward.order_id
            : willCancel
              ? "Stock Transfer cancelled successfully : id - " +
                selectedOutward.id +
                (res.message?.match(/ Linked Reverse DC.*$/)?.[0] ?? "")
              : "Stock Transfer rejected successfully : id - " +
                selectedOutward.id,
      });

      onSuccess?.();
      onClose();
    } catch (err: any) {
      showToast({
        type: ToastType.error,
        text: err?.message || "Something went wrong while rejecting the order.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      id="warehouse-finance-reject-st-modal"
      className="fixed inset-0 z-50 flex items-start justify-center bg-black bg-opacity-40 overflow-y-auto p-4 sm:p-8"
    >
      <div className="relative w-full sm:max-w-2xl max-h-[85vh] bg-white shadow-lg flex flex-col rounded-lg wh-modal-xs">
        <div className="border-b px-6 py-4">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-red-600 ">
            <Icon path={mdiCloseCircleOutline} size={1} color={"red"} />
            Reject Order
          </h2>
          <p id="radix-r10h" className="text-sm text-muted-foreground">
            You are about to reject{" "}
            <span className="font-semibold text-foreground">
              {selectedOutward.type === "SO"
                ? selectedOutward.order_id
                : selectedOutward.id}
            </span>
          </p>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5 space-y-6">
          {selectedOutward.type === "SO" && isFullyAllocated && (
            <div className="flex items-start gap-3 rounded-lg border info-card-red p-3">
              <Icon path={mdiAlertOutline} size={1} />

              <div className="text-sm">
                <p className="font-semibold">Sales Order Rejection</p>
                <p className="mt-1">
                  This Sales Order will be marked as rejected. The revision
                  process must be handled outside this system.
                </p>
                <p className="mt-1 font-semibold">
                  The {allocationSummary.allocated} allocated device IDs will be
                  released and available for other orders.
                </p>
              </div>
            </div>
          )}

          {isRegularST && isPureRMSt && !rmAlreadyReserved && (
            <div className="flex items-start gap-3 rounded-lg border info-card-blue p-3">
              <Icon path={mdiAlertOutline} size={1} />

              <div className="text-sm">
                <p className="font-semibold">Regular ST Rejection</p>
                <p className="mt-1">
                  No material has been picked yet for this stock transfer. The
                  ST creator will be notified and can edit the request before
                  resubmitting.
                </p>
              </div>
            </div>
          )}

          {isRegularST && isPureRMSt && rmAlreadyReserved && (
            <div className="flex items-start gap-3 rounded-lg border warning-card-yellow p-3">
              <Icon path={mdiLockOpenOutline} size={1} />

              <div className="text-sm">
                <p className="font-semibold">
                  {rmReservedQty} unit{rmReservedQty === 1 ? "" : "s"} already
                  picked
                </p>
                <p className="mt-1 text-amber-700 dark:text-amber-400">
                  This material will be released back to available stock. The ST
                  creator will be notified and can edit the request before
                  resubmitting.
                </p>
              </div>
            </div>
          )}

          {isRegularST && !isPureRMSt && !isFullyAllocated && (
            <div className="flex items-start gap-3 rounded-lg border info-card-blue p-3">
              <Icon path={mdiAlertOutline} size={1} />

              <div className="text-sm">
                <p className="font-semibold">Regular ST Rejection</p>
                <p className="mt-1">
                  The ST creator will be notified and can edit the request
                  before resubmitting.
                </p>
              </div>
            </div>
          )}

          {isRegularST && !isPureRMSt && isFullyAllocated && (
            <div className="flex items-start gap-3 rounded-lg border warning-card-yellow p-3">
              <Icon path={mdiLockOpenOutline} size={1} />

              <div className="text-sm">
                <p className="font-semibold">
                  {allocationSummary.allocated} Device IDs Allocated
                </p>
                <p className="mt-1 text-amber-700 dark:text-amber-400">
                  These device IDs will be released back to available stock. The
                  ST creator will be notified and can edit the request before
                  resubmitting.
                </p>
              </div>
            </div>
          )}

          {isRegularST && hasRawMaterialItems && hasFinishedGoodsItems && (
            <div className="flex items-start gap-3 rounded-lg border warning-card-yellow p-3">
              <Icon path={mdiLockOpenOutline} size={1} />

              <div className="text-sm">
                <p className="font-semibold">
                  {rmReservedQty} raw material unit
                  {rmReservedQty === 1 ? "" : "s"} reserved
                </p>
                <p className="mt-1 text-amber-700 dark:text-amber-400">
                  This material will also be released back to available stock.
                </p>
              </div>
            </div>
          )}

          {isReturnableST && (
            <div className="flex items-start gap-3 rounded-lg border warning-card-yellow p-3">
              <Icon path={mdiAlertOutline} size={1} />

              <div className="text-sm">
                <p className="font-semibold">Returnable ST Rejection</p>
                <p className="mt-1">
                  This Returnable ST will be moved directly to Cancelled state.
                </p>
                <p className="mt-1 font-semibold">
                  No further edits will be allowed after this action.
                </p>
              </div>
            </div>
          )}

          {isReplacementST && (
            <div className="flex items-start gap-3 rounded-lg border warning-card-green p-3">
              <Icon path={mdiLockOpenOutline} size={1} />

              <div className="text-sm">
                <p className="font-semibold">Replacement ST Rejection</p>
                <p className="mt-1">
                  This Replacement ST will be moved directly to Cancelled state.
                </p>
                {hasFinishedGoodsItems && (
                  <p className="mt-1 font-semibold">
                    {allocationSummary.allocated} allocated device IDs will be
                    released immediately.
                  </p>
                )}
                {hasRawMaterialItems && (
                  <p className="mt-1 font-semibold">
                    {rmReservedQty} unit{rmReservedQty === 1 ? "" : "s"} of
                    material will be released immediately.
                  </p>
                )}
              </div>
            </div>
          )}

          {selectedOutward.type === "SO" && isFullyAllocated && (
            <div className="flex items-start gap-3 rounded-lg border warning-card-green p-3">
              <Icon path={mdiLockOpenOutline} size={1} />

              <div className="text-sm">
                <p className="font-semibold text-amber-800 dark:text-amber-300">
                  {allocationSummary.allocated} Device IDs Allocated
                </p>
                <p className="mt-1 text-amber-700 dark:text-amber-400">
                  These device IDs will be RELEASED and become available for
                  other orders immediately.
                </p>
              </div>
            </div>
          )}

          <div className="space-y-2">
            <label className="text-base font-semibold">
              Rejection Reason <span className="text-red-600">*</span>
            </label>

            <textarea
              id="warehouse-finance-reject-st-reason-textarea"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={4}
              className="w-full rounded-md border px-3 py-2 text-sm"
              placeholder="Explain why this order is being rejected..."
            />

            <p className="text-xs text-muted-foreground">
              Minimum 10 characters required.
            </p>

            {error && (
              <p className="text-sm text-red-600 font-medium">{error}</p>
            )}
          </div>

          <div className="rounded-lg border p-3 space-y-1.5">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Order Type:</span>
              <span className="font-semibold">
                {selectedOutward.type === "SO"
                  ? "Sales Order"
                  : isRegularST
                    ? "Regular ST"
                    : isReturnableST
                      ? "Returnable ST"
                      : isReplacementST
                        ? "Replacement ST"
                        : "Stock Transfer"}
              </span>
            </div>

            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Total Value:</span>
              <span className="font-semibold">
                {"\u20B9"}{" "}
                {formatAmountWithCommas(
                  getTotalValueFromItems(selectedOutward) || 0,
                )}
              </span>
            </div>

            {selectedOutward?.items?.length > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Items:</span>
                <span className="font-semibold">
                  {selectedOutward?.items?.length || 0} item(s)
                </span>
              </div>
            )}

            {hasFinishedGoodsItems && (
              <div className="flex justify-between text-sm border-t pt-1.5 mt-1.5">
                <span className="text-muted-foreground">Device IDs:</span>
                <span className="font-semibold flex items-center gap-1 text-green-700">
                  <Icon path={mdiLockOpenOutline} size={0.7} />
                  {allocationSummary.allocated} (will release)
                </span>
              </div>
            )}

            {hasRawMaterialItems && (
              <div
                className={`flex justify-between text-sm ${
                  hasFinishedGoodsItems ? "" : "border-t pt-1.5 mt-1.5"
                }`}
              >
                <span className="text-muted-foreground">Material Qty:</span>
                {rmAlreadyReserved ? (
                  <span className="font-semibold flex items-center gap-1 text-green-700">
                    <Icon path={mdiLockOpenOutline} size={0.7} />
                    {rmReservedQty} (will release)
                  </span>
                ) : (
                  <span className="font-semibold text-gray-500">
                    Not yet picked
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="border-t px-6 py-4 flex justify-end gap-2">
          <button
            id="warehouse-finance-reject-st-cancel-btn"
            onClick={handleCancelST}
            disabled={loading}
            className="h-10 rounded-md border px-4 py-2 text-sm font-semibold"
          >
            Cancel
          </button>

          {isRegularST && (
            <button
              id="warehouse-finance-reject-st-reject-cancel-btn"
              onClick={() => handleRejectST(true)}
              disabled={isRejectDisabled}
              className={`h-10 rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white transition-all ${
                isRejectDisabled
                  ? "opacity-50 cursor-not-allowed"
                  : "hover:bg-red-700"
              }`}
            >
              {loading ? "Processing..." : "Reject & Cancel ST"}
            </button>
          )}

          <button
            id="warehouse-finance-reject-st-submit-btn"
            onClick={() => handleRejectST(false)}
            disabled={isRejectDisabled}
            className={`h-10 rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white transition-all ${
              isRejectDisabled
                ? "opacity-50 cursor-not-allowed"
                : "hover:bg-red-700"
            }`}
          >
            {loading
              ? "Processing..."
              : finalStatus === "Cancelled"
                ? "Reject & Cancel"
                : "Reject Order"}
          </button>
        </div>
      </div>

      {loading && <LoadingWidget />}
    </div>
  );
};

export default RejectSTModal;
